import { SDK_AD_EVENT_SET, SDK_LIMITS, SDK_ERRORS } from '@riz/contracts/sdk';
import { rizError, toRizError, type RizError } from './errors.ts';
import type { Operation } from './protocol.ts';
import type { Transport } from './transport.ts';

/** Advertising telemetry has its own allowance; see SDK_AD_EVENTS. */
const METERED_APART = SDK_AD_EVENT_SET;

const MAX_ATTEMPTS = 5;
const RATE_LIMIT_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 8_000;

interface QueueItem {
  operation: Operation;
  attempts: number;
  resolve: (value: Record<string, unknown>) => void;
  reject: (error: RizError) => void;
}

export interface QueueLimits {
  maxEventsPerSession: number;
  maxEventsPerMinute: number;
  maxAdEventsPerSession: number;
}

/**
 * Serial, at-most-once-accepted delivery. Ordering matters because the server
 * validates event sequence, so exactly one request is in flight at a time and
 * a retry re-sends the same `eventId` rather than a new one — the server's
 * idempotency check then collapses a duplicate rather than double-counting it.
 */
export class EventQueue {
  private readonly items: QueueItem[] = [];
  private inFlight: QueueItem | null = null;
  private gameplayCount = 0;
  private adCount = 0;
  private window = { start: Date.now(), count: 0 };
  private draining = false;
  private closed: RizError | null = null;
  private limits: QueueLimits;
  private readonly transport: Transport;

  constructor(transport: Transport, limits?: Partial<QueueLimits>) {
    this.transport = transport;
    this.limits = {
      maxEventsPerSession: limits?.maxEventsPerSession ?? SDK_LIMITS.maxEventsPerSession,
      maxEventsPerMinute: limits?.maxEventsPerMinute ?? SDK_LIMITS.maxEventsPerMinute,
      maxAdEventsPerSession: limits?.maxAdEventsPerSession ?? SDK_LIMITS.maxAdEventsPerSession,
    };
  }

  enqueue(operation: Operation): Promise<Record<string, unknown>> {
    if (this.closed) return Promise.reject(this.closed);
    return new Promise<Record<string, unknown>>((resolve, reject) => {
      this.items.push({ operation, attempts: 0, resolve, reject });
      void this.drain();
    });
  }

  /** Outstanding work, for the page-teardown beacon. */
  pending(): Operation[] {
    const outstanding = this.inFlight ? [this.inFlight.operation] : [];
    return [...outstanding, ...this.items.map((item) => item.operation)];
  }

  close(error: RizError): void {
    this.closed = error;
    const queued = this.items.splice(0, this.items.length);
    for (const item of queued) item.reject(error);
  }

  private budgetFor(name: string) {
    return METERED_APART.has(name)
      ? { used: this.adCount, ceiling: this.limits.maxAdEventsPerSession }
      : { used: this.gameplayCount, ceiling: this.limits.maxEventsPerSession };
  }

  private countDelivered(name: string) {
    if (METERED_APART.has(name)) this.adCount += 1;
    else this.gameplayCount += 1;
  }

  private throttleDelay(): number {
    const now = Date.now();
    if (now - this.window.start >= 60_000) {
      this.window = { start: now, count: 0 };
      return 0;
    }
    if (this.window.count < this.limits.maxEventsPerMinute) return 0;
    return this.window.start + 60_000 - now;
  }

  private async drain(): Promise<void> {
    if (this.draining || this.inFlight || !this.items.length || this.closed) return;
    this.draining = true;
    try {
      while (this.items.length && !this.closed) {
        const next = this.items[0];
        const budget = this.budgetFor(next.operation.name);
        if (budget.used >= budget.ceiling) {
          this.items.shift();
          next.reject(rizError(
            SDK_ERRORS.sessionInactive,
            `This run reached its event budget of ${budget.ceiling}.`,
          ));
          continue;
        }

        const wait = this.throttleDelay();
        if (wait > 0) {
          this.draining = false;
          setTimeout(() => void this.drain(), wait);
          return;
        }

        this.items.shift();
        const settled = await this.deliver(next);
        if (!settled) {
          // Requeued at the head with a backoff already scheduled.
          this.draining = false;
          return;
        }
      }
    } finally {
      this.draining = false;
    }
  }

  /** Resolves true when the item is finished with, false when it was requeued. */
  private async deliver(item: QueueItem): Promise<boolean> {
    this.inFlight = item;
    item.attempts += 1;
    this.window.count += 1;

    try {
      const result = await this.transport.send(item.operation);
      this.inFlight = null;
      this.countDelivered(item.operation.name);
      item.resolve(result);
      return true;
    } catch (raw) {
      this.inFlight = null;
      const error = toRizError(raw);

      if (error.fatal) {
        item.reject(error);
        this.close(error);
        return true;
      }
      if (error.permanent || item.attempts >= MAX_ATTEMPTS) {
        item.reject(error);
        return true;
      }

      this.items.unshift(item);
      const backoff = error.code === SDK_ERRORS.rateLimited
        ? RATE_LIMIT_BACKOFF_MS * item.attempts
        : Math.min(MAX_BACKOFF_MS, 400 * 2 ** item.attempts);
      setTimeout(() => void this.drain(), backoff);
      return false;
    }
  }
}
